import { serializeCookieHeader } from '@supabase/ssr';

// Temporary (sc-536). Before 1.26 server actions wrote the Supabase session cookie
// host-only while the browser client and the proxy wrote it on the shared domain, so a
// device that stayed signed in across the upgrade can send two copies under one name.
// The browser reads the first and the server the last, so they refresh with different
// tokens; the stale one was rotated long ago, Supabase treats it as reuse, revokes the
// session and rate-limits the device (production, 2026-09-23: kitchen kiosks and phones
// stuck in a sign-in loop).
//
// Which copy is live cannot be told safely (they may even belong to different people on a
// shared terminal), so none is used: the request is served without a session and every
// copy is expired. The user signs in once and ends up with a single cookie.
//
// Still needed on @supabase/ssr 0.12.7. Server cookie stores (`cookies()`,
// `request.cookies`) keep one value per name, so a server client's `getAll` never sees
// the second copy and the library cannot withhold or expire it. The 0.12 browser client
// does clear host-only copies, but only on its own writes (a refresh or a sign-out), and
// the proxy may already have refreshed with the stale copy by then. Remove this once
// Supabase logs no more `refresh_token_already_used` and the proxy logs no more
// duplicates.

type Cookie = { name: string; value: string };

/**
 * The name of THIS project's session cookie, derived exactly as supabase-js derives its
 * storage key: `sb-<first label of the Supabase host>-auth-token`. Chunks append `.0`,
 * `.1`, ... to it. Exact on purpose: staging and production share the `.bite-club.app`
 * cookie domain and differ only by project ref, and `-code-verifier` (a PKCE flow,
 * possibly in flight in another tab) is not a session.
 */
export function authTokenCookieName(): string | null {
  try {
    const host = new URL(process.env.NEXT_PUBLIC_SUPABASE_URL ?? '').hostname;
    return host ? `sb-${host.split('.')[0]}-auth-token` : null;
  } catch {
    return null;
  }
}

/** The session cookie itself or one of its chunks. */
export function isSessionCookieName(base: string, name: string): boolean {
  // Chunks are `.0`, `.1`, ... exactly as @supabase/ssr names them (no leading zeros).
  return (
    name === base || (name.startsWith(base) && /^\.(0|[1-9]\d*)$/.test(name.slice(base.length)))
  );
}

/**
 * The session cookie names in a Cookie header when it carries two copies of the session
 * (a repeated name, or the base name next to chunks); null otherwise. Parsed by hand
 * because every cookie parser keeps only one value per name.
 */
export function duplicatedSessionCookieNames(header: string | null | undefined): string[] | null {
  const base = authTokenCookieName();
  if (!header || !base) return null;

  const names = header
    .split(';')
    .map((part) => part.slice(0, part.indexOf('=')).trim())
    .filter((name) => name && isSessionCookieName(base, name));
  const unique = [...new Set(names)];
  const repeated = unique.length !== names.length;
  const mixed = unique.includes(base) && unique.length > 1;
  return repeated || mixed ? unique : null;
}

/**
 * `cookies` without the session when the header carries two copies of it, on hosts with a
 * shared cookie domain (`cookieDomain` starting with a dot): only there does the proxy also
 * expire the copies, so only there may the session be withheld.
 */
export function withoutDuplicatedSession(
  header: string | null | undefined,
  cookies: Cookie[],
  cookieDomain: string | undefined,
): Cookie[] {
  if (!cookieDomain?.startsWith('.')) return cookies;
  const duplicated = duplicatedSessionCookieNames(header);
  if (!duplicated) return cookies;
  return cookies.filter((cookie) => !duplicated.includes(cookie.name));
}

/**
 * `Set-Cookie` headers that expire every copy of the session: without a Domain (the
 * legacy host-only copy) and on `domain` (the current one). Empty when the header does
 * not carry two copies.
 */
export function duplicatedSessionExpiries(
  header: string | null | undefined,
  { domain, secure }: { domain: string; secure: boolean },
): string[] {
  const duplicated = duplicatedSessionCookieNames(header);
  if (!duplicated) return [];
  const expire = { path: '/', sameSite: 'lax' as const, secure, maxAge: 0 };
  return duplicated.flatMap((name) => [
    serializeCookieHeader(name, '', expire),
    serializeCookieHeader(name, '', { ...expire, domain }),
  ]);
}
